import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaKey, FaEnvelope, FaLock, FaTimes } from 'react-icons/fa';
import API_BASE_URL from '../config';
import "./ForgotPassword.css";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // Step 1: Request OTP, Step 2: Verify & Reset
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    // Step 1: Send OTP Request
    const handleSendOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();

            if (response.ok && data.success) {
                setMessage(data.message || "OTP sent successfully to your email!");
                setStep(2); // Move to OTP verification step
            } else {
                setError(data.message || "Failed to send OTP.");
            }
        } catch (err) {
            setError("Server error. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Reset Password with OTP
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp, new_password: newPassword }),
            });
            const data = await response.json();

            if (response.ok && data.success) {
                alert("Password reset successfully! Please login with your new password.");
                navigate('/login'); // Redirect to login page
            } else {
                setError(data.message || "Invalid OTP or failed to reset.");
            }
        } catch (err) {
            setError("Server error. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fp-page-container">
            <div className="fp-modal-box">
                <div className="fp-modal-header">
                    <div className="fp-header-title-area">
                        <div className="fp-top-icon">
                            <FaKey />
                        </div>
                        <h2 className="fp-heading">Password Recovery</h2>
                    </div>
                    <button className="fp-close-btn" onClick={() => navigate('/login')} title="Close">
                        <FaTimes />
                    </button>
                </div>

                {error && <div className="fp-alert fp-alert-error">{error}</div>}
                {message && <div className="fp-alert fp-alert-success">{message}</div>}

                {step === 1 ? (
                    /* Step 1 Form: Enter Email */
                    <form onSubmit={handleSendOtp} className="fp-form">
                        <p className="fp-instruction">
                            Enter your registered email address below, and we'll send you a 6-digit OTP code to reset your password.
                        </p>
                        
                        <div className="fp-input-group">
                            <FaEnvelope className="fp-input-icon" />
                            <input
                                type="email"
                                className="fp-input-field"
                                placeholder="Enter your email address"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="fp-button-container">
                            <button type="submit" className="fp-btn-primary" disabled={loading}>
                                {loading ? 'Sending OTP...' : 'Send OTP'}
                            </button>
                            <button type="button" className="fp-btn-secondary" onClick={() => navigate('/login')}>
                                Back to Login
                            </button>
                        </div>
                    </form>
                ) : (
                    /* Step 2 Form: Enter OTP & New Password */
                    <form onSubmit={handleResetPassword} className="fp-form">
                        <p className="fp-instruction">
                            Enter the 6-digit OTP sent to <b>{email}</b> and your new secure password.
                        </p>

                        <div className="fp-input-group">
                            <FaKey className="fp-input-icon" />
                            <input
                                type="text"
                                className="fp-input-field"
                                placeholder="Enter 6-digit OTP"
                                maxLength="6"
                                required
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                            />
                        </div>

                        <div className="fp-input-group">
                            <FaLock className="fp-input-icon" />
                            <input
                                type="password"
                                className="fp-input-field"
                                placeholder="Enter new password"
                                required
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </div>

                        <div className="fp-button-container">
                            <button type="submit" className="fp-btn-primary" disabled={loading}>
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                            <button type="button" className="fp-btn-secondary" onClick={() => setStep(1)}>
                                Change Email
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;